import { ADMIN_KEY, API_BASE_URL } from '../config';
import type { Chapter, Game, GameAsset, GameModule } from '../types/game';

interface ApiEnvelope<T> { data: T }
interface ApiErrorEnvelope { error?: { message?: string } }

export class ApiRequestError extends Error {
  constructor(message: string, public readonly status: number) { super(message); }
}

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  const headers = new Headers(init?.headers);
  headers.set('Accept', 'application/json');
  if (!(init?.body instanceof FormData)) headers.set('Content-Type', 'application/json');
  if (ADMIN_KEY) headers.set('X-Admin-Key', ADMIN_KEY);
  const response = await fetch(`${API_BASE_URL}${path}`, {
    ...init,
    credentials: 'include',
    headers,
  });
  const payload = (await response.json().catch(() => ({}))) as ApiEnvelope<T> & ApiErrorEnvelope;
  if (!response.ok) throw new ApiRequestError(payload.error?.message || 'No pudimos conectar con el juego.', response.status);
  return payload.data;
}

export const api = {
  game: (slug: string) => request<Game>(`/games/${encodeURIComponent(slug)}`),
  adminGame: (slug: string) => request<Game>(`/admin/games/${encodeURIComponent(slug)}`),
  adminMe: () => request<{ authenticated: boolean; username: string }>('/auth/me'),
  adminLogin: (username: string, password: string) => request<{ authenticated: boolean; username: string }>('/auth/login', { method: 'POST', body: JSON.stringify({ username, password }) }),
  adminLogout: () => request<{ authenticated: boolean }>('/auth/logout', { method: 'POST' }),
  validateAnswer: (moduleId: number, answer: string) =>
    request<{ correct: boolean }>(`/modules/${moduleId}/validate`, {
      method: 'POST',
      body: JSON.stringify({ answer }),
    }),
  createModule: (slug: string, payload: AdminModulePayload) => request<GameModule>(`/admin/games/${encodeURIComponent(slug)}/modules`, { method: 'POST', body: JSON.stringify(payload) }),
  updateModule: (slug: string, id: number, payload: AdminModulePayload) => request<GameModule>(`/admin/games/${encodeURIComponent(slug)}/modules/${id}`, { method: 'PUT', body: JSON.stringify(payload) }),
  deleteModule: (slug: string, id: number) => request<{ deleted: boolean }>(`/admin/games/${encodeURIComponent(slug)}/modules/${id}`, { method: 'DELETE' }),
  uploadAsset: (slug: string, file: File) => { const body = new FormData(); body.append('file', file); return request<GameAsset>(`/admin/games/${encodeURIComponent(slug)}/assets`, { method: 'POST', body }); },
  deleteAsset: (slug: string, id: number) => request<{ deleted: boolean }>(`/admin/games/${encodeURIComponent(slug)}/assets/${id}`, { method: 'DELETE' }),
  createChapter: (slug: string, payload: AdminChapterPayload) => request<Chapter>(`/admin/games/${encodeURIComponent(slug)}/chapters`, { method: 'POST', body: JSON.stringify(payload) }),
  updateChapter: (slug: string, id: number, payload: AdminChapterPayload) => request<Chapter>(`/admin/games/${encodeURIComponent(slug)}/chapters/${id}`, { method: 'PUT', body: JSON.stringify(payload) }),
  deleteChapter: (slug: string, id: number) => request<{ deleted: boolean }>(`/admin/games/${encodeURIComponent(slug)}/chapters/${id}`, { method: 'DELETE' }),
};

export interface AdminModulePayload {
  chapterId: number;
  type: GameModule['type'];
  title: string;
  config: Record<string, unknown>;
  validation: Record<string, unknown> | null;
  reward: Record<string, unknown> | null;
  position: number;
  required: boolean;
}

export interface AdminChapterPayload { title: string; description: string | null; position: number }
